import { apiGet, apiPost } from './client';
import type { OperationResult } from './types';

// Agent Studio "Action policies" — ai.AgentActionPolicy (vNEXT RestApi.Tenant AgentActionPolicyController).
// Tier / role numbers mirror OO.Domain AgentActionTier / AgentDecisionRole.

export type ActionTier = 0 | 1 | 2 | 3 | 4;

export interface ActionPolicyVersion {
  id: number;
  merchantId: number;
  tier: ActionTier;
  tierName: string;
  deciderRole?: number | null;
  deciderRoleName?: string | null;
  standingRulesAllowed: number;
  undoWindowMinutes?: number | null;
  isActive: boolean;
  version: number;
  signed: boolean;
  signedByName?: string | null;
  signedAtUtc?: string | null;
  changedAtUtc?: string | null;
}

export interface ActionPolicyRow {
  kind: string;
  label: string;
  /** An action an agent calls in chat (true) or a clinical request kind (false). */
  isChatTool: boolean;
  tools: string[];
  platform?: ActionPolicyVersion | null;
  practice?: ActionPolicyVersion | null;
  appliesFrom: 'practice' | 'platform' | 'none';
  effectiveTier?: ActionTier | null;
  effectiveTierName?: string | null;
  /** What happens today, in plain words. */
  today: string;
}

export interface ActionPolicyView {
  merchantId: number;
  rows: ActionPolicyRow[];
  tiers: Array<{ value: ActionTier; name: string; chatToday: string }>;
  roles: Array<{ value: number; name: string }>;
}

export interface ActionPolicyListResult {
  view: ActionPolicyView;
  /** Only an Agent Studio admin may change or sign. */
  canEdit: boolean;
}

export interface SaveActionPolicy {
  merchantId: number;
  kind: string;
  tier: ActionTier;
  deciderRole?: number | null;
  standingRulesAllowed?: number;
  undoWindowMinutes?: number | null;
  isActive: boolean;
}

export async function listActionPolicies(merchantId: number): Promise<ActionPolicyListResult> {
  const wrapped = await apiGet<OperationResult<ActionPolicyListResult>>('/api/AgentActionPolicy/List', { merchantId });
  if (!wrapped.isSuccess || !wrapped.data) throw new Error(wrapped.message || 'Failed to load action policies');
  return wrapped.data;
}

export async function saveActionPolicy(body: SaveActionPolicy): Promise<ActionPolicyVersion> {
  const wrapped = await apiPost<OperationResult<ActionPolicyVersion>>('/api/AgentActionPolicy/Save', {
    standingRulesAllowed: 0,
    ...body,
  });
  if (!wrapped.isSuccess || !wrapped.data) throw new Error(wrapped.message || 'Failed to save the action policy');
  return wrapped.data;
}

/** Signs exactly the version the signer was shown. */
export async function signActionPolicy(merchantId: number, kind: string, version: number): Promise<ActionPolicyVersion> {
  const wrapped = await apiPost<OperationResult<ActionPolicyVersion>>('/api/AgentActionPolicy/Sign', { merchantId, kind, version });
  if (!wrapped.isSuccess || !wrapped.data) throw new Error(wrapped.message || 'Failed to sign the action policy');
  return wrapped.data;
}
